import React, { useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaCalendarAlt,
  FaCheckCircle,
  FaFilePdf,
  FaImage,
  FaMagic,
  FaPalette,
  FaSave,
  FaSearch,
  FaSyncAlt,
  FaUpload,
} from "react-icons/fa";
import jsPDF from "jspdf";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { errorAlert, successAlert } from "../../utils/alert";

const LOGO_STRIP = [
  {
    src: `${process.env.PUBLIC_URL || ""}/images/parasports.png`,
    alt: "Parasports Association of Andhra Pradesh",
    label: "Parasports Association of Andhra Pradesh",
  },
  {
    src: `${process.env.PUBLIC_URL || ""}/images/saaplogo.png`,
    alt: "Sports Authority of Andhra Pradesh",
    label: "Sports Authority of Andhra Pradesh",
  },
  {
    src: `${process.env.PUBLIC_URL || ""}/images/pcilogo.png`,
    alt: "Paralympic Committee of India",
    label: "Paralympic Committee of India",
  },
];

const buildTheme = (config) => ({
  label: config.label,
  background: config.background,
  surface: config.surface || "rgba(15, 23, 42, 0.26)",
  secondarySurface: config.secondarySurface || "rgba(255, 255, 255, 0.12)",
  badgeBg: config.badgeBg || "rgba(255, 255, 255, 0.14)",
  badgeText: config.badgeText || "#f8fafc",
  borderColor: config.borderColor || "rgba(255, 255, 255, 0.18)",
  glowOne: config.glowOne || "rgba(255, 255, 255, 0.18)",
  glowTwo: config.glowTwo || "rgba(255, 255, 255, 0.12)",
  titleColor: config.titleColor || "#ffffff",
  textColor: config.textColor || "#f8fafc",
  mutedText: config.mutedText || "rgba(248, 250, 252, 0.76)",
  signatureBg: config.signatureBg || "rgba(255, 255, 255, 0.1)",
  notificationShell: config.notificationShell || "#0f172a",
  notificationCard: config.notificationCard || "#ffffff",
  notificationText: config.notificationText || "#111827",
});

const THEME_PRESETS = {
  celebration: buildTheme({
    label: "Celebration Glow",
    background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 52%, #38bdf8 100%)",
    glowOne: "rgba(59, 130, 246, 0.34)",
    glowTwo: "rgba(34, 211, 238, 0.28)",
  }),
  harvest: buildTheme({
    label: "Harvest Gold",
    background: "linear-gradient(140deg, #4a1d06 0%, #b45309 48%, #fbbf24 100%)",
    glowOne: "rgba(251, 191, 36, 0.38)",
    glowTwo: "rgba(249, 115, 22, 0.28)",
  }),
  patriotic: buildTheme({
    label: "Tricolour Tribute",
    background: "linear-gradient(135deg, #0f172a 0%, #f97316 44%, #f8fafc 60%, #16a34a 100%)",
    surface: "rgba(15, 23, 42, 0.34)",
    secondarySurface: "rgba(255, 255, 255, 0.16)",
    badgeBg: "rgba(15, 23, 42, 0.18)",
    badgeText: "#f8fafc",
    borderColor: "rgba(255, 255, 255, 0.24)",
    titleColor: "#f8fafc",
    textColor: "#f8fafc",
    mutedText: "rgba(248, 250, 252, 0.82)",
    glowOne: "rgba(249, 115, 22, 0.26)",
    glowTwo: "rgba(22, 163, 74, 0.24)",
  }),
  devotion: buildTheme({
    label: "Temple Evening",
    background: "linear-gradient(145deg, #1e1b4b 0%, #7c3aed 45%, #f59e0b 100%)",
    glowOne: "rgba(124, 58, 237, 0.34)",
    glowTwo: "rgba(245, 158, 11, 0.24)",
  }),
  colors: buildTheme({
    label: "Festival of Colours",
    background: "linear-gradient(140deg, #be185d 0%, #7c3aed 28%, #0ea5e9 60%, #f97316 100%)",
    glowOne: "rgba(236, 72, 153, 0.26)",
    glowTwo: "rgba(14, 165, 233, 0.24)",
  }),
  renewal: buildTheme({
    label: "Spring Renewal",
    background: "linear-gradient(135deg, #14532d 0%, #16a34a 36%, #facc15 100%)",
    glowOne: "rgba(34, 197, 94, 0.28)",
    glowTwo: "rgba(250, 204, 21, 0.24)",
  }),
  crescent: buildTheme({
    label: "Moonlit Blessings",
    background: "linear-gradient(145deg, #082f49 0%, #0369a1 44%, #0f766e 100%)",
    glowOne: "rgba(56, 189, 248, 0.28)",
    glowTwo: "rgba(20, 184, 166, 0.26)",
  }),
  wisdom: buildTheme({
    label: "Guiding Light",
    background: "linear-gradient(145deg, #312e81 0%, #2563eb 46%, #f8fafc 100%)",
    surface: "rgba(30, 41, 59, 0.34)",
    secondarySurface: "rgba(255, 255, 255, 0.16)",
    badgeBg: "rgba(30, 41, 59, 0.18)",
    badgeText: "#ffffff",
    borderColor: "rgba(255, 255, 255, 0.22)",
    titleColor: "#f8fafc",
    textColor: "#f8fafc",
    mutedText: "rgba(241, 245, 249, 0.84)",
    glowOne: "rgba(37, 99, 235, 0.26)",
    glowTwo: "rgba(255, 255, 255, 0.18)",
  }),
  bond: buildTheme({
    label: "Warm Bond",
    background: "linear-gradient(145deg, #7c2d12 0%, #dc2626 48%, #fb7185 100%)",
    glowOne: "rgba(220, 38, 38, 0.28)",
    glowTwo: "rgba(251, 113, 133, 0.24)",
  }),
  elephant: buildTheme({
    label: "Auspicious Dawn",
    background: "linear-gradient(140deg, #581c87 0%, #c026d3 38%, #f59e0b 100%)",
    glowOne: "rgba(192, 38, 211, 0.24)",
    glowTwo: "rgba(245, 158, 11, 0.24)",
  }),
  lights: buildTheme({
    label: "Lamp Glow",
    background: "linear-gradient(145deg, #431407 0%, #b45309 44%, #facc15 100%)",
    glowOne: "rgba(251, 191, 36, 0.34)",
    glowTwo: "rgba(253, 224, 71, 0.26)",
  }),
  winter: buildTheme({
    label: "Winter Classic",
    background: "linear-gradient(140deg, #0f172a 0%, #0f766e 38%, #e2e8f0 100%)",
    surface: "rgba(15, 23, 42, 0.3)",
    secondarySurface: "rgba(255, 255, 255, 0.18)",
    badgeBg: "rgba(15, 23, 42, 0.16)",
    badgeText: "#ffffff",
    titleColor: "#f8fafc",
    textColor: "#f8fafc",
    mutedText: "rgba(241, 245, 249, 0.82)",
    glowOne: "rgba(15, 118, 110, 0.28)",
    glowTwo: "rgba(226, 232, 240, 0.18)",
  }),
  earth: buildTheme({
    label: "Earth Care",
    background: "linear-gradient(140deg, #14532d 0%, #0f766e 36%, #84cc16 100%)",
    glowOne: "rgba(34, 197, 94, 0.26)",
    glowTwo: "rgba(132, 204, 22, 0.24)",
  }),
  wellness: buildTheme({
    label: "Calm Wellness",
    background: "linear-gradient(140deg, #0f172a 0%, #0284c7 42%, #2dd4bf 100%)",
    glowOne: "rgba(2, 132, 199, 0.26)",
    glowTwo: "rgba(45, 212, 191, 0.24)",
  }),
  awareness: buildTheme({
    label: "Awareness Spotlight",
    background: "linear-gradient(140deg, #1f2937 0%, #7c3aed 38%, #f43f5e 100%)",
    glowOne: "rgba(124, 58, 237, 0.26)",
    glowTwo: "rgba(244, 63, 94, 0.24)",
  }),
  honour: buildTheme({
    label: "Honour Frame",
    background: "linear-gradient(140deg, #1f2937 0%, #334155 32%, #0ea5e9 100%)",
    glowOne: "rgba(14, 165, 233, 0.24)",
    glowTwo: "rgba(148, 163, 184, 0.18)",
  }),
  knowledge: buildTheme({
    label: "Knowledge Spark",
    background: "linear-gradient(145deg, #172554 0%, #4338ca 38%, #22c55e 100%)",
    glowOne: "rgba(67, 56, 202, 0.26)",
    glowTwo: "rgba(34, 197, 94, 0.22)",
  }),
  inclusion: buildTheme({
    label: "Inclusive Future",
    background: "linear-gradient(140deg, #111827 0%, #7c3aed 34%, #06b6d4 100%)",
    glowOne: "rgba(124, 58, 237, 0.26)",
    glowTwo: "rgba(6, 182, 212, 0.22)",
  }),
  creative: buildTheme({
    label: "Creative Lens",
    background: "linear-gradient(145deg, #111827 0%, #7c2d12 30%, #ec4899 72%, #f59e0b 100%)",
    glowOne: "rgba(236, 72, 153, 0.26)",
    glowTwo: "rgba(245, 158, 11, 0.24)",
  }),
  journey: buildTheme({
    label: "Journey Vista",
    background: "linear-gradient(145deg, #0f172a 0%, #0369a1 35%, #65a30d 100%)",
    glowOne: "rgba(3, 105, 161, 0.26)",
    glowTwo: "rgba(101, 163, 13, 0.22)",
  }),
  sport: buildTheme({
    label: "Champion Track",
    background: "linear-gradient(145deg, #111827 0%, #1d4ed8 32%, #ef4444 70%, #f59e0b 100%)",
    glowOne: "rgba(29, 78, 216, 0.24)",
    glowTwo: "rgba(239, 68, 68, 0.24)",
  }),
  victory: buildTheme({
    label: "Victory Banner",
    background: "linear-gradient(145deg, #3f0d12 0%, #a21caf 38%, #f59e0b 100%)",
    glowOne: "rgba(162, 28, 175, 0.24)",
    glowTwo: "rgba(245, 158, 11, 0.24)",
  }),
  heritage: buildTheme({
    label: "Heritage Stone",
    background: "linear-gradient(145deg, #1c1917 0%, #92400e 42%, #0f766e 100%)",
    glowOne: "rgba(146, 64, 14, 0.24)",
    glowTwo: "rgba(15, 118, 110, 0.2)",
  }),
};

const THEME_VISUALS = {
  celebration: {
    artFamily: "festival",
    exportGradient: ["#0f172a", "#1d4ed8", "#38bdf8"],
    artPrimary: "rgba(255,255,255,0.52)",
    artSecondary: "rgba(255,255,255,0.20)",
    artAccent: "rgba(34,211,238,0.26)",
  },
  harvest: {
    artFamily: "harvest",
    exportGradient: ["#4a1d06", "#b45309", "#fbbf24"],
    artPrimary: "rgba(255,248,220,0.54)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(251,191,36,0.24)",
  },
  patriotic: {
    artFamily: "patriotic",
    exportGradient: ["#0f172a", "#f97316", "#16a34a"],
    artPrimary: "rgba(255,255,255,0.42)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(37,99,235,0.24)",
  },
  devotion: {
    artFamily: "devotion",
    exportGradient: ["#1e1b4b", "#7c3aed", "#f59e0b"],
    artPrimary: "rgba(255,255,255,0.48)",
    artSecondary: "rgba(255,255,255,0.14)",
    artAccent: "rgba(245,158,11,0.22)",
  },
  colors: {
    artFamily: "colors",
    exportGradient: ["#be185d", "#7c3aed", "#0ea5e9", "#f97316"],
    artPrimary: "rgba(255,255,255,0.32)",
    artSecondary: "rgba(255,255,255,0.14)",
    artAccent: "rgba(251,191,36,0.20)",
  },
  renewal: {
    artFamily: "renewal",
    exportGradient: ["#14532d", "#16a34a", "#facc15"],
    artPrimary: "rgba(255,255,255,0.44)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(250,204,21,0.18)",
  },
  crescent: {
    artFamily: "crescent",
    exportGradient: ["#082f49", "#0369a1", "#0f766e"],
    artPrimary: "rgba(255,255,255,0.52)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(56,189,248,0.20)",
  },
  wisdom: {
    artFamily: "wisdom",
    exportGradient: ["#312e81", "#2563eb", "#f8fafc"],
    artPrimary: "rgba(255,255,255,0.50)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(37,99,235,0.22)",
  },
  bond: {
    artFamily: "bond",
    exportGradient: ["#7c2d12", "#dc2626", "#fb7185"],
    artPrimary: "rgba(255,255,255,0.44)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(251,113,133,0.20)",
  },
  elephant: {
    artFamily: "elephant",
    exportGradient: ["#581c87", "#c026d3", "#f59e0b"],
    artPrimary: "rgba(255,255,255,0.48)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(245,158,11,0.20)",
  },
  lights: {
    artFamily: "lights",
    exportGradient: ["#431407", "#b45309", "#facc15"],
    artPrimary: "rgba(255,255,255,0.50)",
    artSecondary: "rgba(255,255,255,0.14)",
    artAccent: "rgba(253,224,71,0.20)",
  },
  winter: {
    artFamily: "winter",
    exportGradient: ["#0f172a", "#0f766e", "#e2e8f0"],
    artPrimary: "rgba(255,255,255,0.54)",
    artSecondary: "rgba(255,255,255,0.20)",
    artAccent: "rgba(148,163,184,0.18)",
  },
  earth: {
    artFamily: "earth",
    exportGradient: ["#14532d", "#0f766e", "#84cc16"],
    artPrimary: "rgba(255,255,255,0.46)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(132,204,22,0.18)",
  },
  wellness: {
    artFamily: "wellness",
    exportGradient: ["#0f172a", "#0284c7", "#2dd4bf"],
    artPrimary: "rgba(255,255,255,0.48)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(45,212,191,0.20)",
  },
  awareness: {
    artFamily: "awareness",
    exportGradient: ["#1f2937", "#7c3aed", "#f43f5e"],
    artPrimary: "rgba(255,255,255,0.46)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(244,63,94,0.22)",
  },
  honour: {
    artFamily: "honour",
    exportGradient: ["#1f2937", "#334155", "#0ea5e9"],
    artPrimary: "rgba(255,255,255,0.48)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(14,165,233,0.18)",
  },
  knowledge: {
    artFamily: "knowledge",
    exportGradient: ["#172554", "#4338ca", "#22c55e"],
    artPrimary: "rgba(255,255,255,0.48)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(34,197,94,0.18)",
  },
  inclusion: {
    artFamily: "inclusion",
    exportGradient: ["#111827", "#7c3aed", "#06b6d4"],
    artPrimary: "rgba(255,255,255,0.50)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(6,182,212,0.18)",
  },
  creative: {
    artFamily: "creative",
    exportGradient: ["#111827", "#7c2d12", "#ec4899", "#f59e0b"],
    artPrimary: "rgba(255,255,255,0.44)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(236,72,153,0.18)",
  },
  journey: {
    artFamily: "journey",
    exportGradient: ["#0f172a", "#0369a1", "#65a30d"],
    artPrimary: "rgba(255,255,255,0.46)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(101,163,13,0.18)",
  },
  sport: {
    artFamily: "sport",
    exportGradient: ["#111827", "#1d4ed8", "#ef4444", "#f59e0b"],
    artPrimary: "rgba(255,255,255,0.46)",
    artSecondary: "rgba(255,255,255,0.18)",
    artAccent: "rgba(245,158,11,0.18)",
  },
  victory: {
    artFamily: "victory",
    exportGradient: ["#3f0d12", "#a21caf", "#f59e0b"],
    artPrimary: "rgba(255,255,255,0.48)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(245,158,11,0.18)",
  },
  heritage: {
    artFamily: "heritage",
    exportGradient: ["#1c1917", "#92400e", "#0f766e"],
    artPrimary: "rgba(255,255,255,0.46)",
    artSecondary: "rgba(255,255,255,0.16)",
    artAccent: "rgba(15,118,110,0.18)",
  },
};

const COMMONS_SEARCH_API = "https://commons.wikimedia.org/w/api.php";
const COMMONS_BACKGROUND_CACHE = new Map();
const COMMONS_EXCLUDED_FILE_PATTERN =
  /\b(logo|banner|poster|symbol|icon|map|stamp|seal|emblem|illustration|drawing|vector|svg)\b/i;

const OCCASION_BACKGROUND_SEARCH_TERMS = {
  makar_sankranti_pongal_lohri: [
    "Pongal harvest festival India",
    "Makar Sankranti festival India",
  ],
  republic_day_india: [
    "Republic Day India parade",
    "India national celebration parade",
  ],
  maha_shivaratri: [
    "Maha Shivaratri temple night India",
    "Shiva temple festival India",
  ],
  holi: ["Holi festival colors India", "Holi celebration India"],
  ugadi_gudi_padwa_vishu_baisakhi_tamil_new_year: [
    "Ugadi festival India",
    "Baisakhi celebration India",
  ],
  ram_navami: ["Ram Navami celebration India", "Lord Rama festival India"],
  good_friday_easter: ["Easter celebration church candles", "Good Friday church prayer"],
  eid_al_fitr: ["Eid al-Fitr celebration lantern mosque", "Eid festival prayer"],
  guru_purnima: ["Guru Purnima lamp prayer", "teacher blessing India"],
  raksha_bandhan: ["Raksha Bandhan festival India", "rakhi celebration India"],
  krishna_janmashtami: ["Janmashtami celebration India", "Krishna temple festival"],
  independence_day_india: [
    "Indian Independence Day celebration flag",
    "India tricolour celebration",
  ],
  ganesh_chaturthi: ["Ganesh Chaturthi festival India", "Ganesha celebration India"],
  gandhi_jayanti: ["Mahatma Gandhi memorial India", "Rajghat Gandhi Jayanti"],
  navratri_durga_puja: ["Durga Puja festival India", "Navratri celebration India"],
  dussehra: ["Dussehra celebration India", "Vijayadashami festival India"],
  diwali: ["Diwali diya lights India", "Diwali festival lamps"],
  christmas: ["Christmas tree lights celebration", "Christmas festive lights"],
  eid_al_adha: ["Eid al-Adha celebration mosque", "Eid prayer festival"],
  world_health_day: ["public health care community", "hospital care people"],
  world_environment_day: ["forest nature sunlight", "green environment trees"],
  international_yoga_day: ["yoga meditation sunrise", "group yoga outdoors"],
  world_population_day: ["city crowd aerial people", "community gathering people"],
  world_mental_health_day: ["calm wellness nature", "support community wellbeing"],
  human_rights_day: ["human rights gathering", "people unity community"],
  international_womens_day: ["women celebration community", "women leadership event"],
  international_mens_day: ["men community leadership", "men group celebration"],
  national_girl_child_day: ["girl child education India", "school girl smiling India"],
  mothers_day: ["mother and child celebration", "mother family love"],
  fathers_day: ["father and child celebration", "father family moment"],
  national_youth_day: ["Swami Vivekananda portrait", "youth gathering India"],
  teachers_day_india: ["teacher classroom India", "education teacher students"],
  childrens_day_india: ["children celebration India", "happy children school"],
  world_disability_day: ["para athlete wheelchair sport", "inclusive community disability"],
  world_autism_awareness_day: ["autism awareness support", "inclusive child support"],
  international_day_of_older_persons: ["elderly community support", "older persons care"],
  labour_day: ["workers labour day", "industrial workers celebration"],
  doctors_day_india: ["doctor hospital care", "medical team hospital"],
  engineers_day_india: ["engineering teamwork", "engineer technology work"],
  world_photography_day: ["photographer camera lens", "camera photography event"],
  world_food_day: ["fresh food produce market", "harvest food celebration"],
  world_tourism_day: ["travel tourism landscape", "tourism landmark vista"],
  world_smile_day: ["happy smiling people", "smile celebration community"],
  army_day_india: ["Indian Army parade", "army ceremony India"],
  national_science_day: ["science laboratory", "science experiment lab"],
  kargil_vijay_diwas: ["Kargil War memorial India", "war memorial India"],
  national_sports_day: ["athletics track stadium", "sports celebration athletes"],
  police_commemoration_day: ["police memorial India", "police honour ceremony"],
};

const THEME_BACKGROUND_SEARCH_TERMS = {
  celebration: ["festival lights celebration", "cultural celebration India"],
  harvest: ["golden harvest field festival", "harvest celebration India"],
  patriotic: ["India flag celebration parade", "patriotic India event"],
  devotion: ["temple lamps devotion India", "spiritual festival India"],
  colors: ["vibrant colour festival India", "festival powder colors"],
  renewal: ["spring celebration flowers sunrise", "new year celebration India"],
  crescent: ["crescent lantern night celebration", "mosque lights evening"],
  wisdom: ["lamp books inspiration", "guidance learning tradition"],
  bond: ["family bond celebration", "festival family togetherness"],
  elephant: ["Ganesh festival India", "auspicious celebration India"],
  lights: ["festival lamp lights India", "glowing diyas night"],
  winter: ["winter festive lights", "Christmas decorations night"],
  earth: ["green nature sunrise", "lush forest landscape"],
  wellness: ["wellness meditation sunrise", "peaceful yoga landscape"],
  awareness: ["community solidarity gathering", "awareness support people"],
  honour: ["tribute memorial flame", "honour ceremony"],
  knowledge: ["books classroom education", "learning inspiration"],
  inclusion: ["inclusive community together", "adaptive sport unity"],
  creative: ["creative photography lights", "camera art festival"],
  journey: ["travel landscape scenic", "tourism destination view"],
  sport: ["stadium athletics action", "sports arena track"],
  victory: ["festival victory celebration", "triumph lights celebration"],
  heritage: ["Indian heritage monument sunrise", "heritage architecture India"],
};

const POSTER_EXPORT_WIDTH = 1600;
const POSTER_EXPORT_HEIGHT = 2000;

const uniqueValues = (values = []) => [
  ...new Set(
    values
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  ),
];

const getThemeVisualConfig = (themeKey = "celebration") => {
  const safeThemeKey = String(themeKey || "celebration").trim() || "celebration";
  const theme = THEME_PRESETS[safeThemeKey] || THEME_PRESETS.celebration;
  const visuals = THEME_VISUALS[safeThemeKey] || THEME_VISUALS.celebration;
  return {
    ...theme,
    ...visuals,
  };
};

const svgToDataUrl = (svgMarkup) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;

const buildPosterBackgroundSearchTerms = (poster) => {
  const occasionKey = String(poster?.occasion_key || "").trim();
  const themeKey = String(poster?.theme_key || "celebration").trim() || "celebration";
  const title = String(poster?.title || "").trim();
  const category = String(poster?.category_label || "").trim();

  return uniqueValues([
    ...(OCCASION_BACKGROUND_SEARCH_TERMS[occasionKey] || []),
    title ? `${title} celebration` : "",
    title ? `${title} India` : "",
    ...(THEME_BACKGROUND_SEARCH_TERMS[themeKey] || THEME_BACKGROUND_SEARCH_TERMS.celebration),
    category ? `${category} celebration` : "",
  ]);
};

const buildArtworkSvg = (theme) => {
  const primary = theme.artPrimary || "rgba(255,255,255,0.46)";
  const secondary = theme.artSecondary || "rgba(255,255,255,0.18)";
  const accent = theme.artAccent || "rgba(255,255,255,0.22)";
  const family = theme.artFamily || "festival";

  const baseStart = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 2000" preserveAspectRatio="none">
      <defs>
        <linearGradient id="fadeOverlay" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${secondary}" />
          <stop offset="100%" stop-color="transparent" />
        </linearGradient>
      </defs>
      <rect width="1600" height="2000" fill="url(#fadeOverlay)" opacity="0.18"/>
  `;

  const baseEnd = "</svg>";

  const svgByFamily = {
    festival: `
      <g fill="${secondary}">
        <circle cx="180" cy="180" r="18"/><circle cx="1450" cy="210" r="20"/><circle cx="1240" cy="420" r="14"/>
        <circle cx="300" cy="1320" r="22"/><circle cx="1390" cy="1470" r="18"/><circle cx="1280" cy="1760" r="24"/>
      </g>
      <g stroke="${primary}" stroke-width="16" fill="none" stroke-linecap="round">
        <path d="M70 340 C280 150 520 220 640 120"/>
        <path d="M940 140 C1130 250 1360 140 1540 240"/>
        <path d="M118 1620 C360 1500 520 1640 700 1550"/>
      </g>
      <g fill="${accent}">
        <path d="M1160 990 l26 76 78 2 -62 45 22 74 -64 -47 -64 47 22 -74 -62 -45 78 -2z"/>
        <path d="M330 620 l18 54 55 2 -44 32 17 53 -46 -34 -46 34 17 -53 -44 -32 55 -2z"/>
      </g>
    `,
    harvest: `
      <circle cx="1270" cy="280" r="150" fill="${accent}"/>
      <g stroke="${primary}" stroke-width="12" fill="none" stroke-linecap="round">
        <path d="M120 1640 C340 1510 620 1490 960 1560 C1180 1610 1380 1580 1520 1490"/>
        <path d="M120 1710 C360 1585 640 1560 1010 1638 C1230 1685 1415 1660 1510 1600"/>
        <path d="M140 1785 C420 1665 720 1655 1090 1720 C1270 1750 1400 1740 1525 1690"/>
      </g>
      <g fill="${secondary}">
        <path d="M230 260 l90 90 -90 40 -90 -40z"/>
        <path d="M230 260 l90 90 55 -120z"/>
        <path d="M450 170 l72 72 -72 34 -72 -34z"/>
        <path d="M450 170 l72 72 42 -96z"/>
      </g>
      <g stroke="${primary}" stroke-width="10" fill="none" stroke-linecap="round">
        <path d="M230 390 L230 620"/>
        <path d="M450 276 L450 520"/>
      </g>
    `,
    patriotic: `
      <g fill="none" stroke="${primary}" stroke-width="12">
        <circle cx="1210" cy="390" r="150"/>
        <circle cx="1210" cy="390" r="20"/>
      </g>
      <g stroke="${primary}" stroke-width="8" stroke-linecap="round">
        <path d="M1210 240 L1210 540"/><path d="M1060 390 L1360 390"/>
        <path d="M1104 284 L1316 496"/><path d="M1316 284 L1104 496"/>
        <path d="M1140 250 L1280 530"/><path d="M1280 250 L1140 530"/>
      </g>
      <path d="M100 1180 C420 920 790 960 1080 1120 C1250 1215 1380 1240 1535 1190" fill="none" stroke="rgba(249,115,22,0.34)" stroke-width="42" stroke-linecap="round"/>
      <path d="M80 1320 C360 1100 760 1110 1060 1260 C1260 1360 1380 1390 1525 1340" fill="none" stroke="rgba(22,163,74,0.30)" stroke-width="42" stroke-linecap="round"/>
    `,
    devotion: `
      <path d="M120 1530 C290 1290 470 1180 660 1120 C820 1070 980 1090 1160 1185 C1290 1250 1400 1355 1520 1550" fill="none" stroke="${primary}" stroke-width="18" stroke-linecap="round"/>
      <path d="M420 1430 C540 1280 700 1200 820 1180 C960 1160 1100 1210 1250 1360" fill="none" stroke="${secondary}" stroke-width="12" stroke-linecap="round"/>
      <circle cx="1320" cy="300" r="120" fill="${accent}"/>
      <circle cx="1360" cy="270" r="104" fill="transparent" stroke="${primary}" stroke-width="16"/>
      <g fill="${secondary}">
        <path d="M310 640 C350 550 420 550 460 640 C438 700 332 700 310 640z"/>
        <path d="M520 520 C560 440 630 440 670 520 C648 580 542 580 520 520z"/>
      </g>
    `,
    colors: `
      <g fill="${accent}">
        <path d="M240 260 C300 150 420 140 500 230 C560 300 560 390 500 450 C420 520 320 500 250 420 C210 370 190 310 240 260z"/>
        <path d="M1040 180 C1140 80 1300 100 1400 210 C1490 305 1480 455 1380 530 C1270 612 1110 590 1030 490 C940 390 940 270 1040 180z"/>
        <path d="M960 1320 C1090 1210 1260 1240 1340 1370 C1420 1490 1390 1670 1270 1740 C1160 1815 990 1770 920 1650 C860 1540 860 1410 960 1320z"/>
      </g>
      <g fill="${secondary}">
        <circle cx="580" cy="820" r="90"/><circle cx="340" cy="1080" r="120"/><circle cx="1200" cy="960" r="80"/>
      </g>
    `,
    renewal: `
      <g fill="none" stroke="${primary}" stroke-width="12" stroke-linecap="round">
        <path d="M250 1700 C330 1460 460 1260 650 1090"/>
        <path d="M1360 1710 C1280 1465 1150 1270 955 1080"/>
      </g>
      <g fill="${secondary}">
        <path d="M580 1180 C630 1090 735 1080 800 1170 C730 1250 645 1265 580 1180z"/>
        <path d="M770 1040 C865 990 950 1040 990 1140 C900 1160 820 1125 770 1040z"/>
        <path d="M960 1210 C1050 1160 1130 1220 1170 1315 C1085 1335 1005 1290 960 1210z"/>
      </g>
      <circle cx="1320" cy="260" r="115" fill="${accent}"/>
    `,
    crescent: `
      <circle cx="1270" cy="300" r="160" fill="${primary}" opacity="0.46"/>
      <circle cx="1330" cy="250" r="142" fill="transparent" stroke="${secondary}" stroke-width="24"/>
      <circle cx="1365" cy="250" r="132" fill="rgba(8,47,73,0.70)"/>
      <g fill="${secondary}">
        <circle cx="1120" cy="540" r="10"/><circle cx="1245" cy="660" r="14"/><circle cx="1390" cy="560" r="12"/><circle cx="1490" cy="470" r="9"/>
      </g>
      <g stroke="${primary}" stroke-width="14" fill="none" stroke-linecap="round">
        <path d="M240 1580 C410 1370 620 1240 820 1210 C1010 1180 1160 1220 1340 1350"/>
        <path d="M540 1470 C650 1360 770 1300 870 1290 C1000 1275 1100 1320 1210 1430"/>
      </g>
    `,
    wisdom: `
      <g stroke="${primary}" stroke-width="10" fill="none" stroke-linecap="round">
        <path d="M800 220 L800 520"/><path d="M650 330 L950 330"/><path d="M695 255 L905 465"/><path d="M905 255 L695 465"/>
      </g>
      <g fill="${secondary}">
        <path d="M500 1350 C610 1250 740 1240 800 1305 C855 1240 995 1245 1110 1350 C995 1400 905 1420 800 1415 C690 1420 600 1400 500 1350z"/>
        <path d="M800 1305 C850 1450 860 1590 850 1700 C820 1715 780 1715 750 1700 C740 1595 745 1450 800 1305z"/>
      </g>
    `,
    bond: `
      <g fill="${secondary}">
        <circle cx="420" cy="320" r="26"/><circle cx="1180" cy="320" r="26"/>
        <circle cx="520" cy="420" r="18"/><circle cx="1080" cy="420" r="18"/>
      </g>
      <g stroke="${primary}" stroke-width="18" fill="none" stroke-linecap="round">
        <path d="M310 420 C430 210 680 210 800 420"/>
        <path d="M800 420 C920 210 1170 210 1290 420"/>
        <path d="M300 1460 C470 1660 680 1680 800 1555 C920 1680 1130 1660 1300 1460"/>
      </g>
    `,
    elephant: `
      <g stroke="${primary}" stroke-width="18" fill="none" stroke-linecap="round">
        <path d="M420 1450 C450 1310 560 1210 700 1210 C825 1210 920 1290 950 1385 C975 1470 950 1595 880 1660"/>
        <path d="M880 1660 C855 1710 855 1760 910 1795"/>
        <path d="M580 1390 C605 1335 675 1320 720 1355"/>
        <path d="M860 1380 C885 1335 950 1330 990 1365"/>
      </g>
      <circle cx="1270" cy="260" r="120" fill="${accent}"/>
      <g fill="${secondary}">
        <circle cx="675" cy="1465" r="16"/><circle cx="940" cy="1455" r="16"/>
      </g>
    `,
    lights: `
      <g stroke="${primary}" stroke-width="12" fill="none" stroke-linecap="round">
        <path d="M100 280 C320 180 520 210 740 290 C950 365 1180 370 1500 240"/>
        <path d="M120 360 C380 245 620 290 860 370 C1070 435 1270 430 1490 330"/>
      </g>
      <g fill="${accent}">
        <circle cx="260" cy="330" r="22"/><circle cx="480" cy="320" r="22"/><circle cx="710" cy="345" r="22"/><circle cx="970" cy="390" r="22"/><circle cx="1230" cy="370" r="22"/>
      </g>
      <g fill="${secondary}">
        <path d="M620 1550 C680 1435 760 1435 820 1550 C785 1630 655 1630 620 1550z"/>
        <path d="M840 1550 C900 1435 980 1435 1040 1550 C1005 1630 875 1630 840 1550z"/>
      </g>
    `,
    winter: `
      <g stroke="${primary}" stroke-width="8" fill="none" stroke-linecap="round">
        <path d="M250 260 L250 400"/><path d="M180 330 L320 330"/><path d="M205 285 L295 375"/><path d="M295 285 L205 375"/>
        <path d="M1280 240 L1280 380"/><path d="M1210 310 L1350 310"/><path d="M1235 265 L1325 355"/><path d="M1325 265 L1235 355"/>
        <path d="M1250 1490 L1250 1630"/><path d="M1180 1560 L1320 1560"/><path d="M1205 1515 L1295 1605"/><path d="M1295 1515 L1205 1605"/>
      </g>
      <g fill="${secondary}">
        <circle cx="420" cy="1280" r="16"/><circle cx="560" cy="1520" r="14"/><circle cx="960" cy="1320" r="18"/><circle cx="760" cy="1700" r="22"/>
      </g>
    `,
    earth: `
      <g stroke="${primary}" stroke-width="14" fill="none" stroke-linecap="round">
        <path d="M160 1700 C430 1450 780 1390 1120 1490 C1290 1540 1410 1590 1520 1690"/>
        <path d="M300 1580 C510 1420 790 1380 1060 1455"/>
      </g>
      <g fill="${secondary}">
        <circle cx="1280" cy="300" r="140"/>
        <path d="M1200 290 C1240 220 1320 220 1360 290 C1325 340 1235 340 1200 290z"/>
        <path d="M1265 430 C1320 360 1400 370 1435 450 C1365 470 1305 460 1265 430z"/>
      </g>
    `,
    wellness: `
      <g fill="${secondary}">
        <path d="M800 1180 C870 1045 970 1030 1050 1150 C970 1200 885 1225 800 1180z"/>
        <path d="M800 1180 C730 1045 630 1030 550 1150 C630 1200 715 1225 800 1180z"/>
        <path d="M800 1280 C910 1185 1030 1220 1100 1360 C990 1395 885 1385 800 1280z"/>
        <path d="M800 1280 C690 1185 570 1220 500 1360 C610 1395 715 1385 800 1280z"/>
      </g>
      <g stroke="${primary}" stroke-width="10" fill="none">
        <circle cx="800" cy="1280" r="170"/><circle cx="800" cy="1280" r="250"/>
      </g>
    `,
    awareness: `
      <g stroke="${primary}" stroke-width="18" fill="none" stroke-linecap="round">
        <path d="M1060 280 C1200 110 1430 170 1420 390 C1410 560 1200 650 1060 470 C930 300 1030 190 1190 190"/>
        <path d="M370 1380 C510 1210 740 1270 730 1490 C720 1660 510 1750 370 1570 C240 1400 340 1290 500 1290"/>
      </g>
      <g fill="${secondary}">
        <circle cx="770" cy="1120" r="26"/><circle cx="970" cy="1320" r="18"/><circle cx="1130" cy="1500" r="22"/>
      </g>
    `,
    honour: `
      <g stroke="${primary}" stroke-width="14" fill="none" stroke-linecap="round">
        <path d="M520 300 C610 190 720 140 800 130 C880 140 990 190 1080 300"/>
        <path d="M540 1680 C650 1780 740 1830 800 1850 C860 1830 950 1780 1060 1680"/>
      </g>
      <g fill="${secondary}">
        <path d="M610 400 C655 310 745 305 800 385 C745 445 665 450 610 400z"/>
        <path d="M800 385 C855 305 945 310 990 400 C935 450 855 445 800 385z"/>
      </g>
    `,
    knowledge: `
      <g stroke="${primary}" stroke-width="12" fill="none" stroke-linecap="round">
        <path d="M800 180 L800 520"/><path d="M650 270 L950 270"/><path d="M700 200 L900 430"/><path d="M900 200 L700 430"/>
      </g>
      <g fill="${secondary}">
        <path d="M530 1410 C650 1280 760 1260 800 1300 C840 1260 950 1280 1070 1410 C960 1455 875 1465 800 1455 C725 1465 640 1455 530 1410z"/>
        <rect x="735" y="1450" width="130" height="260" rx="32" />
      </g>
    `,
    inclusion: `
      <g fill="${secondary}">
        <circle cx="430" cy="430" r="95"/><circle cx="1170" cy="430" r="95"/><circle cx="800" cy="1520" r="118"/>
      </g>
      <g stroke="${primary}" stroke-width="16" fill="none" stroke-linecap="round">
        <path d="M430 430 C540 250 700 250 800 430 C900 250 1060 250 1170 430"/>
        <path d="M430 430 C520 700 690 910 800 1520 C910 910 1080 700 1170 430"/>
      </g>
    `,
    creative: `
      <g fill="none" stroke="${primary}" stroke-width="16">
        <circle cx="1220" cy="320" r="165"/><circle cx="1220" cy="320" r="80"/>
      </g>
      <g fill="${secondary}">
        <path d="M1220 155 L1265 245 L1360 260 L1292 330 L1310 425 L1220 380 L1130 425 L1148 330 L1080 260 L1175 245z"/>
      </g>
      <g stroke="${accent}" stroke-width="30" fill="none" stroke-linecap="round">
        <path d="M190 1580 C480 1280 720 1220 960 1330"/>
        <path d="M420 1760 C730 1480 980 1440 1320 1540"/>
      </g>
    `,
    journey: `
      <g fill="${secondary}">
        <path d="M140 1630 L430 1110 L730 1630z"/>
        <path d="M560 1630 L860 980 L1180 1630z"/>
        <path d="M950 1630 L1210 1210 L1490 1630z"/>
      </g>
      <path d="M170 1820 C420 1660 570 1500 720 1360 C890 1210 1050 1170 1320 1240" fill="none" stroke="${primary}" stroke-width="18" stroke-linecap="round"/>
      <circle cx="1270" cy="270" r="112" fill="${accent}"/>
    `,
    sport: `
      <g stroke="${primary}" stroke-width="24" fill="none" stroke-linecap="round">
        <path d="M120 1710 C420 1510 780 1490 1150 1560 C1290 1585 1410 1615 1500 1650"/>
        <path d="M120 1780 C420 1580 780 1560 1150 1630 C1290 1655 1410 1685 1500 1720"/>
        <path d="M120 1850 C420 1650 780 1630 1150 1700 C1290 1725 1410 1755 1500 1790"/>
      </g>
      <g fill="${secondary}">
        <circle cx="1230" cy="360" r="110"/><circle cx="1120" cy="480" r="70"/><circle cx="1340" cy="480" r="70"/>
      </g>
    `,
    victory: `
      <g fill="${secondary}">
        <path d="M620 280 L710 390 L800 320 L890 390 L980 280 L950 470 L840 430 L800 520 L760 430 L650 470z"/>
      </g>
      <g stroke="${primary}" stroke-width="14" fill="none" stroke-linecap="round">
        <path d="M380 1590 C520 1740 690 1820 800 1840 C910 1820 1080 1740 1220 1590"/>
        <path d="M460 1510 C580 1640 710 1705 800 1715 C890 1705 1020 1640 1140 1510"/>
      </g>
    `,
    heritage: `
      <g stroke="${primary}" stroke-width="16" fill="none" stroke-linecap="round">
        <path d="M360 480 C440 320 610 220 800 210 C990 220 1160 320 1240 480"/>
        <path d="M520 480 L520 740"/><path d="M800 420 L800 760"/><path d="M1080 480 L1080 740"/>
      </g>
      <g fill="${secondary}">
        <circle cx="800" cy="1280" r="145"/><circle cx="800" cy="1280" r="68"/>
      </g>
    `,
  };

  return `${baseStart}${svgByFamily[family] || svgByFamily.festival}${baseEnd}`;
};

const getThemeArtworkDataUrl = (theme) => svgToDataUrl(buildArtworkSvg(theme));

const fetchCommonsBackgroundFromSearch = async (searchTerm) => {
  const searchVariants = uniqueValues([`${searchTerm} filetype:bitmap`, searchTerm]);

  for (const variant of searchVariants) {
    const requestUrl = new URL(COMMONS_SEARCH_API);
    requestUrl.searchParams.set("action", "query");
    requestUrl.searchParams.set("format", "json");
    requestUrl.searchParams.set("generator", "search");
    requestUrl.searchParams.set("gsrnamespace", "6");
    requestUrl.searchParams.set("gsrlimit", "8");
    requestUrl.searchParams.set("gsrsearch", variant);
    requestUrl.searchParams.set("prop", "imageinfo");
    requestUrl.searchParams.set("iiprop", "url|size");
    requestUrl.searchParams.set("iiurlwidth", "2200");
    requestUrl.searchParams.set("iiurlheight", "2600");
    requestUrl.searchParams.set("origin", "*");

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: "omit",
      });
      if (!response.ok) {
        continue;
      }

      const payload = await response.json();
      const candidates = Object.values(payload?.query?.pages || {})
        .map((page) => ({
          title: String(page?.title || ""),
          info: page?.imageinfo?.[0] || null,
        }))
        .filter((candidate) => candidate.info?.thumburl || candidate.info?.url)
        .filter((candidate) => !COMMONS_EXCLUDED_FILE_PATTERN.test(candidate.title))
        .sort((left, right) => {
          const leftArea = Number(left.info?.width || 0) * Number(left.info?.height || 0);
          const rightArea = Number(right.info?.width || 0) * Number(right.info?.height || 0);
          return rightArea - leftArea;
        });

      const bestMatch = candidates[0];
      if (bestMatch?.info?.thumburl || bestMatch?.info?.url) {
        return bestMatch.info.thumburl || bestMatch.info.url || "";
      }
    } catch (_error) {
      // Best-effort only. The poster falls back to gradients if no remote image is found.
    }
  }

  return "";
};

const resolvePosterBackgroundUrl = async (poster) => {
  const cacheKey = `${String(poster?.occasion_key || "default")}::${String(
    poster?.theme_key || "celebration"
  )}`;
  if (COMMONS_BACKGROUND_CACHE.has(cacheKey)) {
    return COMMONS_BACKGROUND_CACHE.get(cacheKey) || "";
  }

  const searchTerms = buildPosterBackgroundSearchTerms(poster);
  for (const searchTerm of searchTerms) {
    const url = await fetchCommonsBackgroundFromSearch(searchTerm);
    if (url) {
      COMMONS_BACKGROUND_CACHE.set(cacheKey, url);
      return url;
    }
  }

  COMMONS_BACKGROUND_CACHE.set(cacheKey, "");
  return "";
};

const createCanvasGradient = (ctx, width, height, colors) => {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  const palette = Array.isArray(colors) && colors.length ? colors : ["#0f172a", "#1d4ed8", "#38bdf8"];
  palette.forEach((color, index) => {
    const stop = palette.length === 1 ? 1 : index / (palette.length - 1);
    gradient.addColorStop(stop, color);
  });
  return gradient;
};

const roundRectPath = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadImageAsset = async (source) => {
  if (!source) return null;

  try {
    const raw = String(source);
    const imageSrc = raw.startsWith("data:")
      ? raw
      : await fetch(raw, { mode: "cors", credentials: "omit" })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Image request failed (${response.status})`);
            }
            return response.blob();
          })
          .then((blob) => blobToDataUrl(blob));

    return await new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = imageSrc;
    });
  } catch (_error) {
    return null;
  }
};

const drawImageCover = (ctx, image, x, y, width, height) => {
  if (!image) return;
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const offsetX = x + (width - drawWidth) / 2;
  const offsetY = y + (height - drawHeight) / 2;
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

const drawImageContain = (ctx, image, x, y, width, height) => {
  if (!image) return;
  const scale = Math.min(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const offsetX = x + (width - drawWidth) / 2;
  const offsetY = y + (height - drawHeight) / 2;
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

const drawImageCoverRounded = (ctx, image, x, y, width, height, radius) => {
  if (!image) return;
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const offsetX = x + (width - drawWidth) / 2;
  const offsetY = y + (height - drawHeight) / 2;

  ctx.save();
  roundRectPath(ctx, x, y, width, height, radius);
  ctx.clip();
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
  ctx.restore();
};

const ellipsizeText = (ctx, value, maxWidth) => {
  const source = String(value || "");
  if (ctx.measureText(source).width <= maxWidth) return source;

  let trimmed = source;
  while (trimmed.length > 1 && ctx.measureText(`${trimmed}...`).width > maxWidth) {
    trimmed = trimmed.slice(0, -1);
  }
  return `${trimmed.trimEnd()}...`;
};

const buildWrappedLines = (ctx, text, maxWidth) => {
  const paragraphs = String(text || "")
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}|\n/g);

  const lines = [];
  paragraphs.forEach((paragraph, paragraphIndex) => {
    const words = String(paragraph || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) {
      if (paragraphIndex < paragraphs.length - 1) lines.push("");
      return;
    }

    let currentLine = words[0];
    for (let index = 1; index < words.length; index += 1) {
      const testLine = `${currentLine} ${words[index]}`;
      if (ctx.measureText(testLine).width <= maxWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = words[index];
      }
    }
    lines.push(currentLine);
    if (paragraphIndex < paragraphs.length - 1) lines.push("");
  });

  return lines.length ? lines : [""];
};

const fitTextLayout = (
  ctx,
  text,
  {
    maxWidth,
    maxHeight,
    maxFontSize,
    minFontSize,
    fontFamily,
    fontWeight = 600,
    lineHeightFactor = 1.28,
    spacerFactor = 0.42,
  }
) => {
  for (let fontSize = maxFontSize; fontSize >= minFontSize; fontSize -= 2) {
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    const lines = buildWrappedLines(ctx, text, maxWidth);
    const lineHeight = fontSize * lineHeightFactor;
    const spacerHeight = lineHeight * spacerFactor;
    const totalHeight = lines.reduce(
      (sum, line) => sum + (line ? lineHeight : spacerHeight),
      0
    );
    if (totalHeight <= maxHeight) {
      return { fontSize, lineHeight, spacerHeight, lines };
    }
  }

  ctx.font = `${fontWeight} ${minFontSize}px ${fontFamily}`;
  const lines = buildWrappedLines(ctx, text, maxWidth);
  const lineHeight = minFontSize * lineHeightFactor;
  const spacerHeight = lineHeight * spacerFactor;
  const fittedLines = [];
  let currentHeight = 0;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const nextHeight = currentHeight + (line ? lineHeight : spacerHeight);
    if (nextHeight > maxHeight && fittedLines.length > 0) {
      const lastLine = fittedLines[fittedLines.length - 1] || "";
      fittedLines[fittedLines.length - 1] = ellipsizeText(ctx, lastLine, maxWidth);
      break;
    }
    fittedLines.push(line);
    currentHeight = nextHeight;
  }

  if (!fittedLines.length) {
    fittedLines.push(ellipsizeText(ctx, String(text || ""), maxWidth));
  }

  return { fontSize: minFontSize, lineHeight, spacerHeight, lines: fittedLines };
};

const drawTextLayout = (ctx, layout, x, y, color) => {
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  let cursorY = y;
  layout.lines.forEach((line) => {
    if (line) {
      ctx.fillText(line, x, cursorY);
      cursorY += layout.lineHeight;
    } else {
      cursorY += layout.spacerHeight;
    }
  });
  return cursorY;
};

const drawCenteredTextLayout = (ctx, layout, centerX, y, color) => {
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  let cursorY = y;
  layout.lines.forEach((line) => {
    if (line) {
      ctx.fillText(line, centerX, cursorY);
      cursorY += layout.lineHeight;
    } else {
      cursorY += layout.spacerHeight;
    }
  });
  ctx.textAlign = "left";
  return cursorY;
};

const downloadBlob = (blob, fileName) => {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
};

const slugifyFileName = (value = "") =>
  String(value || "occasion-poster")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "occasion-poster";

const getPosterTextPalette = (theme, hasBackgroundImage) => ({
  primary: hasBackgroundImage ? "#ffffff" : theme.titleColor,
  secondary: hasBackgroundImage ? "rgba(255,255,255,0.88)" : theme.textColor,
  tertiary: hasBackgroundImage ? "rgba(255,255,255,0.74)" : theme.mutedText,
  shadow: hasBackgroundImage ? "rgba(0,0,0,0.56)" : "rgba(15,23,42,0.24)",
});

const getPosterArtDirection = (poster) => {
  const themeKey = String(poster?.theme_key || "");

  if (["patriotic", "honour", "sport", "victory", "heritage"].includes(themeKey)) {
    return {
      layout: "center",
      previewTitleFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
      canvasTitleFamily: "Arial",
      titleWeight: 800,
      titleLetterSpacing: "0.04em",
      dividerWidth: 180,
    };
  }

  if (["harvest", "bond", "lights", "winter", "celebration", "colors", "renewal"].includes(themeKey)) {
    return {
      layout: "left",
      previewTitleFamily: "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive",
      canvasTitleFamily: "cursive",
      titleWeight: 700,
      titleLetterSpacing: "0.01em",
      dividerWidth: 220,
    };
  }

  return {
    layout: "left",
    previewTitleFamily: "'Palatino Linotype', 'Book Antiqua', Georgia, serif",
    canvasTitleFamily: "Georgia",
    titleWeight: 700,
    titleLetterSpacing: "0.02em",
    dividerWidth: 170,
  };
};

const drawCircularPortrait = (ctx, image, centerX, topY, size, fallbackName, textPalette) => {
  const imageX = centerX - size / 2;

  if (image) {
    drawImageCoverRounded(ctx, image, imageX, topY, size, size, size / 2);
  } else {
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, topY + size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = "rgba(255,255,255,0.14)";
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = textPalette.primary;
    ctx.font = "700 72px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(getInitials(fallbackName), centerX, topY + size / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, topY + size / 2, size / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.lineWidth = 8;
  ctx.strokeStyle = "rgba(255,255,255,0.84)";
  ctx.shadowColor = "rgba(0,0,0,0.26)";
  ctx.shadowBlur = 16;
  ctx.stroke();
  ctx.restore();
};

const renderPosterCanvas = async ({
  poster,
  leaderOnePreviewSrc,
  leaderTwoPreviewSrc,
  backgroundPreviewSrc,
}) => {
  const theme = getThemeVisualConfig(poster?.theme_key);
  const artworkUrl = getThemeArtworkDataUrl(theme);
  const canvas = document.createElement("canvas");
  canvas.width = POSTER_EXPORT_WIDTH;
  canvas.height = POSTER_EXPORT_HEIGHT;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas is not available in this browser.");
  }

  const resolvedBackgroundUrl = backgroundPreviewSrc || (await resolvePosterBackgroundUrl(poster));
  const [backgroundImage, artworkImage, leaderOneImage, leaderTwoImage, ...logoImages] = await Promise.all([
    loadImageAsset(resolvedBackgroundUrl),
    loadImageAsset(artworkUrl),
    loadImageAsset(leaderOnePreviewSrc),
    loadImageAsset(leaderTwoPreviewSrc),
    ...LOGO_STRIP.map((item) => loadImageAsset(item.src)),
  ]);
  const textPalette = getPosterTextPalette(theme, Boolean(backgroundImage));
  const artDirection = getPosterArtDirection(poster);
  const isCenteredPoster = artDirection.layout === "center";

  if (backgroundImage) {
    drawImageCover(ctx, backgroundImage, 0, 0, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT);
  } else {
    ctx.fillStyle = createCanvasGradient(
      ctx,
      POSTER_EXPORT_WIDTH,
      POSTER_EXPORT_HEIGHT,
      theme.exportGradient
    );
    ctx.fillRect(0, 0, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT);
  }

  if (artworkImage) {
    ctx.save();
    ctx.globalAlpha = backgroundImage ? 0.16 : 0.36;
    ctx.drawImage(artworkImage, 0, 0, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT);
    ctx.restore();
  }

  const topFade = ctx.createLinearGradient(0, 0, 0, 560);
  topFade.addColorStop(0, "rgba(2,6,23,0.68)");
  topFade.addColorStop(0.42, "rgba(2,6,23,0.24)");
  topFade.addColorStop(1, "rgba(2,6,23,0)");
  ctx.fillStyle = topFade;
  ctx.fillRect(0, 0, POSTER_EXPORT_WIDTH, 560);

  const bottomFade = ctx.createLinearGradient(0, 760, 0, POSTER_EXPORT_HEIGHT);
  bottomFade.addColorStop(0, "rgba(2,6,23,0)");
  bottomFade.addColorStop(0.3, "rgba(2,6,23,0.34)");
  bottomFade.addColorStop(1, "rgba(2,6,23,0.92)");
  ctx.fillStyle = bottomFade;
  ctx.fillRect(0, 760, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT - 760);

  if (!isCenteredPoster) {
    const leftShade = ctx.createLinearGradient(0, 0, POSTER_EXPORT_WIDTH * 0.74, 0);
    leftShade.addColorStop(0, "rgba(2,6,23,0.74)");
    leftShade.addColorStop(0.52, "rgba(2,6,23,0.30)");
    leftShade.addColorStop(1, "rgba(2,6,23,0)");
    ctx.fillStyle = leftShade;
    ctx.fillRect(0, 0, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT);
  }

  const glowLeft = ctx.createRadialGradient(300, 240, 0, 300, 240, 560);
  glowLeft.addColorStop(0, theme.glowOne);
  glowLeft.addColorStop(1, "transparent");
  ctx.fillStyle = glowLeft;
  ctx.fillRect(0, 0, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT);

  const glowRight = ctx.createRadialGradient(1280, 240, 0, 1280, 240, 560);
  glowRight.addColorStop(0, theme.glowTwo);
  glowRight.addColorStop(1, "transparent");
  ctx.fillStyle = glowRight;
  ctx.fillRect(0, 0, POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT);

  const logoSize = 164;
  const logoGap = 100;
  const totalLogosWidth = logoSize * LOGO_STRIP.length + logoGap * (LOGO_STRIP.length - 1);
  const logosStartX = (POSTER_EXPORT_WIDTH - totalLogosWidth) / 2;
  const logosY = 72;

  LOGO_STRIP.forEach((logo, index) => {
    const logoImage = logoImages[index];
    if (!logoImage) return;

    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.28)";
    ctx.shadowBlur = 18;
    drawImageContain(
      ctx,
      logoImage,
      logosStartX + index * (logoSize + logoGap),
      logosY,
      logoSize,
      logoSize
    );
    ctx.restore();
  });

  ctx.save();
  ctx.shadowColor = textPalette.shadow;
  ctx.shadowBlur = 18;
  ctx.fillStyle = textPalette.tertiary;
  ctx.font = "700 22px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("PARASPORTS ASSOCIATION OF ANDHRA PRADESH", POSTER_EXPORT_WIDTH / 2, 258);
  const metaLine = [poster.date_label, poster.category_label, poster.quarter_label]
    .filter(Boolean)
    .join("  •  ");
  const titleText = poster.title || "Occasion Title";
  const taglineText =
    poster.tagline || "Create a memorable greeting and poster layout for this occasion.";
  const bodyText =
    poster.resolved_message ||
    "The automatic poster message will appear here when no custom text is provided.";

  let leadersTop = 1510;
  let leaderOneCenterX = POSTER_EXPORT_WIDTH / 2 - 220;
  let leaderTwoCenterX = POSTER_EXPORT_WIDTH / 2 + 220;
  let portraitSize = 220;

  if (isCenteredPoster) {
    ctx.font = "700 20px Arial";
    ctx.fillText(metaLine, POSTER_EXPORT_WIDTH / 2, 300);
    ctx.font = "700 20px Arial";
    ctx.fillText("SPECIAL WISHES", POSTER_EXPORT_WIDTH / 2, 352);

    const titleLayout = fitTextLayout(ctx, titleText, {
      maxWidth: POSTER_EXPORT_WIDTH - 220,
      maxHeight: 220,
      maxFontSize: 90,
      minFontSize: 60,
      fontFamily: artDirection.canvasTitleFamily,
      fontWeight: artDirection.titleWeight,
      lineHeightFactor: 1.02,
    });
    ctx.font = `${artDirection.titleWeight} ${titleLayout.fontSize}px ${artDirection.canvasTitleFamily}`;
    const titleEndY = drawCenteredTextLayout(
      ctx,
      titleLayout,
      POSTER_EXPORT_WIDTH / 2,
      406,
      textPalette.primary
    );

    ctx.strokeStyle = "rgba(255,255,255,0.56)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(POSTER_EXPORT_WIDTH / 2 - artDirection.dividerWidth / 2, titleEndY + 18);
    ctx.lineTo(POSTER_EXPORT_WIDTH / 2 + artDirection.dividerWidth / 2, titleEndY + 18);
    ctx.stroke();

    const taglineLayout = fitTextLayout(ctx, taglineText, {
      maxWidth: POSTER_EXPORT_WIDTH - 360,
      maxHeight: 96,
      maxFontSize: 30,
      minFontSize: 22,
      fontFamily: "Arial",
      fontWeight: 600,
      lineHeightFactor: 1.24,
    });
    ctx.font = `600 ${taglineLayout.fontSize}px Arial`;
    const taglineEndY = drawCenteredTextLayout(
      ctx,
      taglineLayout,
      POSTER_EXPORT_WIDTH / 2,
      titleEndY + 34,
      textPalette.secondary
    );

    const messageLayout = fitTextLayout(ctx, bodyText, {
      maxWidth: POSTER_EXPORT_WIDTH - 320,
      maxHeight: Math.max(260, leadersTop - taglineEndY - 110),
      maxFontSize: 40,
      minFontSize: 24,
      fontFamily: "Arial",
      fontWeight: 500,
      lineHeightFactor: 1.4,
      spacerFactor: 0.64,
    });
    ctx.font = `500 ${messageLayout.fontSize}px Arial`;
    drawCenteredTextLayout(
      ctx,
      messageLayout,
      POSTER_EXPORT_WIDTH / 2,
      taglineEndY + 48,
      textPalette.secondary
    );
  } else {
    const contentX = 110;
    const contentWidth = 820;

    ctx.textAlign = "left";
    ctx.font = "700 18px Arial";
    ctx.fillText(metaLine, contentX, 332);
    ctx.font = "700 18px Arial";
    ctx.fillText("SPECIAL WISHES", contentX, 368);

    const titleLayout = fitTextLayout(ctx, titleText, {
      maxWidth: contentWidth,
      maxHeight: 220,
      maxFontSize: 96,
      minFontSize: 60,
      fontFamily: artDirection.canvasTitleFamily,
      fontWeight: artDirection.titleWeight,
      lineHeightFactor: 1.02,
    });
    ctx.font = `${artDirection.titleWeight} ${titleLayout.fontSize}px ${artDirection.canvasTitleFamily}`;
    const titleEndY = drawTextLayout(ctx, titleLayout, contentX, 404, textPalette.primary);

    ctx.strokeStyle = "rgba(255,255,255,0.56)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(contentX, titleEndY + 18);
    ctx.lineTo(contentX + artDirection.dividerWidth, titleEndY + 18);
    ctx.stroke();

    const taglineLayout = fitTextLayout(ctx, taglineText, {
      maxWidth: contentWidth - 60,
      maxHeight: 96,
      maxFontSize: 30,
      minFontSize: 22,
      fontFamily: "Georgia",
      fontWeight: 600,
      lineHeightFactor: 1.24,
    });
    ctx.font = `600 ${taglineLayout.fontSize}px Georgia`;
    drawTextLayout(ctx, taglineLayout, contentX, titleEndY + 34, textPalette.secondary);

    leadersTop = 1526;
    portraitSize = 184;
    leaderOneCenterX = 1135;
    leaderTwoCenterX = 1370;

    const messageLayout = fitTextLayout(ctx, bodyText, {
      maxWidth: 860,
      maxHeight: 300,
      maxFontSize: 36,
      minFontSize: 24,
      fontFamily: "Arial",
      fontWeight: 500,
      lineHeightFactor: 1.38,
      spacerFactor: 0.64,
    });
    ctx.font = `500 ${messageLayout.fontSize}px Arial`;
    drawTextLayout(ctx, messageLayout, contentX, 1260, textPalette.secondary);
  }
  ctx.restore();

  drawCircularPortrait(
    ctx,
    leaderOneImage,
    leaderOneCenterX,
    leadersTop,
    portraitSize,
    poster.leader_one_name || "Leader Name",
    textPalette
  );
  drawCircularPortrait(
    ctx,
    leaderTwoImage,
    leaderTwoCenterX,
    leadersTop,
    portraitSize,
    poster.leader_two_name || "Leader Name",
    textPalette
  );

  ctx.save();
  ctx.shadowColor = textPalette.shadow;
  ctx.shadowBlur = 14;

  const leaderNameY = leadersTop + portraitSize + 30;
  ctx.fillStyle = textPalette.primary;
  ctx.font = isCenteredPoster ? "700 34px Arial" : "700 28px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(poster.leader_one_name || "Leader Name", leaderOneCenterX, leaderNameY);
  ctx.fillText(poster.leader_two_name || "Leader Name", leaderTwoCenterX, leaderNameY);

  const leaderOneTitleLayout = fitTextLayout(ctx, poster.leader_one_title || "Leader Title", {
    maxWidth: isCenteredPoster ? 320 : 250,
    maxHeight: 82,
    maxFontSize: isCenteredPoster ? 22 : 18,
    minFontSize: 16,
    fontFamily: "Arial",
    fontWeight: 600,
    lineHeightFactor: 1.24,
  });
  ctx.font = `600 ${leaderOneTitleLayout.fontSize}px Arial`;
  drawCenteredTextLayout(
    ctx,
    leaderOneTitleLayout,
    leaderOneCenterX,
    leaderNameY + 48,
    textPalette.tertiary
  );

  const leaderTwoTitleLayout = fitTextLayout(ctx, poster.leader_two_title || "Leader Title", {
    maxWidth: 320,
    maxHeight: 82,
    maxFontSize: 22,
    minFontSize: 16,
    fontFamily: "Arial",
    fontWeight: 600,
    lineHeightFactor: 1.24,
  });
  ctx.font = `600 ${leaderTwoTitleLayout.fontSize}px Arial`;
  drawCenteredTextLayout(
    ctx,
    leaderTwoTitleLayout,
    leaderTwoCenterX,
    leaderNameY + 48,
    textPalette.tertiary
  );

  ctx.font = "700 20px Arial";
  ctx.fillStyle = textPalette.tertiary;
  ctx.fillText("With warm wishes from Parasports Association of Andhra Pradesh", POSTER_EXPORT_WIDTH / 2, 1918);
  ctx.restore();

  return canvas;
};

const INITIAL_FORM = {
  tagline: "",
  theme_key: "celebration",
  custom_message: "",
  custom_notification_message: "",
  remove_background_image: false,
  leader_one_name: "",
  leader_one_title: "",
  leader_two_name: "",
  leader_two_title: "",
  remove_leader_one_photo: false,
  remove_leader_two_photo: false,
  is_active: true,
};

const normalizeSearchToken = (value = "") =>
  String(value || "").trim().toLowerCase();

const buildFormFromPoster = (poster) => ({
  tagline: String(poster?.tagline || ""),
  theme_key: String(poster?.theme_key || "celebration"),
  custom_message: String(poster?.custom_message || ""),
  custom_notification_message: String(poster?.custom_notification_message || ""),
  remove_background_image: false,
  leader_one_name: String(poster?.leader_one_name || ""),
  leader_one_title: String(poster?.leader_one_title || ""),
  leader_two_name: String(poster?.leader_two_name || ""),
  leader_two_title: String(poster?.leader_two_title || ""),
  remove_leader_one_photo: false,
  remove_leader_two_photo: false,
  is_active: Boolean(poster?.is_active),
});

const themeLabel = (themeKey = "") =>
  THEME_PRESETS[String(themeKey || "").trim()]?.label ||
  String(themeKey || "celebration")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getInitials = (value = "") => {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "PA";
  return parts.map((part) => part[0]).join("").toUpperCase();
};

const FileUploadHint = ({ children }) => (
  <p className="mt-1 text-[11px] text-slate-500">{children}</p>
);

const PosterLeaderFigure = ({ name, title, photoSrc, textPalette }) => (
  <div className="w-[124px] text-center sm:w-[156px]">
    {photoSrc ? (
      <img
        src={photoSrc}
        alt={name || "Leader"}
        className="mx-auto h-24 w-24 rounded-full border-4 object-cover shadow-[0_18px_40px_rgba(15,23,42,0.32)] sm:h-32 sm:w-32"
        style={{ borderColor: "rgba(255,255,255,0.84)" }}
      />
    ) : (
      <div
        className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 text-2xl font-bold shadow-[0_18px_40px_rgba(15,23,42,0.32)] sm:h-32 sm:w-32"
        style={{
          borderColor: "rgba(255,255,255,0.84)",
          background: "rgba(255,255,255,0.16)",
          color: textPalette.primary,
        }}
      >
        {getInitials(name)}
      </div>
    )}
    <p
      className="mt-3 text-sm font-semibold leading-tight sm:text-base"
      style={{ color: textPalette.primary, textShadow: `0 6px 22px ${textPalette.shadow}` }}
    >
      {name || "Leader Name"}
    </p>
    <p
      className="mt-1 text-[11px] leading-5 sm:text-xs"
      style={{ color: textPalette.tertiary, textShadow: `0 4px 18px ${textPalette.shadow}` }}
    >
      {title || "Leader Title"}
    </p>
  </div>
);

const OccasionPosterPreview = ({
  poster,
  leaderOnePreviewSrc,
  leaderTwoPreviewSrc,
  backgroundPreviewSrc,
  backgroundLoading,
}) => {
  const theme = getThemeVisualConfig(poster?.theme_key);
  const artworkUrl = getThemeArtworkDataUrl(theme);
  const textPalette = getPosterTextPalette(theme, Boolean(backgroundPreviewSrc));
  const artDirection = getPosterArtDirection(poster);
  const isCentered = artDirection.layout === "center";
  const layoutMeta = [poster.date_label, poster.category_label, poster.quarter_label]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="space-y-5 2xl:sticky 2xl:top-6">
      <div
        className="overflow-hidden rounded-[34px] border shadow-[0_30px_70px_rgba(15,23,42,0.24)]"
        style={{
          borderColor: "rgba(15, 23, 42, 0.08)",
          background: theme.background,
        }}
      >
        <div
          className="relative aspect-[4/5] overflow-hidden"
          style={{ background: theme.background }}
        >
          {backgroundPreviewSrc ? (
            <img
              src={backgroundPreviewSrc}
              alt={`${poster.title || "Occasion"} background`}
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          <img
            src={artworkUrl}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={{ mixBlendMode: "screen", opacity: backgroundPreviewSrc ? 0.18 : 0.44 }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: isCentered
                ? `linear-gradient(180deg, rgba(2,6,23,0.28) 0%, rgba(2,6,23,0.18) 26%, rgba(2,6,23,0.82) 100%), radial-gradient(circle at 16% 12%, ${theme.glowOne} 0%, transparent 34%), radial-gradient(circle at 88% 14%, ${theme.glowTwo} 0%, transparent 34%)`
                : `linear-gradient(90deg, rgba(2,6,23,0.68) 0%, rgba(2,6,23,0.32) 42%, rgba(2,6,23,0.06) 72%), linear-gradient(180deg, rgba(2,6,23,0.16) 0%, rgba(2,6,23,0.84) 100%), radial-gradient(circle at 14% 12%, ${theme.glowOne} 0%, transparent 30%), radial-gradient(circle at 90% 16%, ${theme.glowTwo} 0%, transparent 30%)`,
            }}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/55 to-transparent" />

          <div className="relative z-10 flex h-full flex-col px-5 py-6 sm:px-8 sm:py-8">
            <div className="flex items-center justify-center gap-3 sm:gap-8">
              {LOGO_STRIP.map((logo) => (
                <img
                  key={logo.label}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-16 w-16 object-contain drop-shadow-[0_16px_30px_rgba(15,23,42,0.34)] sm:h-24 sm:w-24"
                />
              ))}
            </div>

            {backgroundLoading ? (
              <div className="mt-4 self-end rounded-full bg-black/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80 backdrop-blur">
                Loading background
              </div>
            ) : null}

            <div className="mt-6 flex flex-1 flex-col">
              <div className={isCentered ? "mx-auto max-w-4xl text-center" : "max-w-[63%] text-left"}>
                <p
                  className="text-[11px] font-semibold uppercase tracking-[0.34em] sm:text-xs"
                  style={{ color: textPalette.tertiary, textShadow: `0 5px 18px ${textPalette.shadow}` }}
                >
                  {layoutMeta || "Special Wishes"}
                </p>
                <h1
                  className={isCentered ? "mt-4 text-4xl leading-none sm:text-[4.35rem]" : "mt-4 text-[3rem] leading-none sm:text-[5rem]"}
                  style={{
                    color: textPalette.primary,
                    fontFamily: artDirection.previewTitleFamily,
                    fontWeight: artDirection.titleWeight,
                    letterSpacing: artDirection.titleLetterSpacing,
                    textShadow: `0 10px 26px ${textPalette.shadow}`,
                  }}
                >
                  {poster.title || "Occasion Title"}
                </h1>
                <div
                  className={`mt-4 h-[2px] rounded-full ${isCentered ? "mx-auto" : ""}`}
                  style={{
                    width: `${Math.round(artDirection.dividerWidth * 0.55)}px`,
                    background: "rgba(255,255,255,0.72)",
                  }}
                />
                <p
                  className={`mt-4 text-base leading-relaxed sm:text-lg ${isCentered ? "mx-auto max-w-3xl" : "max-w-[92%]"}`}
                  style={{
                    color: textPalette.secondary,
                    textShadow: `0 6px 22px ${textPalette.shadow}`,
                  }}
                >
                  {poster.tagline || "Create a memorable greeting and poster layout for this occasion."}
                </p>
              </div>

              <div className={`${isCentered ? "mx-auto max-w-3xl text-center" : "mt-auto max-w-[58%] text-left"}`}>
                <p
                  className={`text-sm leading-7 sm:text-[1.02rem] ${isCentered ? "mt-10" : "mt-auto"}`}
                  style={{
                    color: textPalette.secondary,
                    textShadow: `0 6px 22px ${textPalette.shadow}`,
                    display: "-webkit-box",
                    WebkitLineClamp: isCentered ? 7 : 6,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {poster.resolved_message ||
                    "The automatic poster message will appear here when no custom text is provided."}
                </p>
              </div>

              <div className={`mt-8 flex flex-wrap gap-5 ${isCentered ? "justify-center" : "justify-end"}`}>
                <PosterLeaderFigure
                  name={poster.leader_one_name}
                  title={poster.leader_one_title}
                  photoSrc={leaderOnePreviewSrc}
                  textPalette={textPalette}
                />
                <PosterLeaderFigure
                  name={poster.leader_two_name}
                  title={poster.leader_two_title}
                  photoSrc={leaderTwoPreviewSrc}
                  textPalette={textPalette}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="rounded-[30px] p-5 shadow-[0_20px_50px_rgba(15,23,42,0.18)]"
        style={{ background: theme.notificationShell }}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400">
              App Notification Preview
            </p>
            <p className="mt-1 text-sm text-slate-300">
              Auto fallback stays active if the short message is left blank.
            </p>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white">
            <FaBell />
          </div>
        </div>

        <div
          className="mt-4 rounded-[26px] p-4 shadow-[0_16px_36px_rgba(15,23,42,0.24)]"
          style={{ background: theme.notificationCard }}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Parasports AP
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {poster.title}
              </p>
            </div>
            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
              {poster.custom_notification_message?.trim() ? "Custom" : "Auto"}
            </span>
          </div>
          <p className="mt-3 text-sm leading-6" style={{ color: theme.notificationText }}>
            {poster.resolved_notification_message ||
              "The automatic notification message will appear here."}
          </p>
        </div>
      </div>
    </div>
  );
};

const LeaderEditor = ({
  heading,
  nameField,
  titleField,
  nameValue,
  titleValue,
  previewSrc,
  hasStoredPhoto,
  removePhoto,
  onFieldChange,
  onFileChange,
  onToggleRemovePhoto,
  onClearSelectedFile,
  selectedFile,
}) => (
  <div className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{heading}</h3>
        <p className="mt-1 text-sm text-slate-500">
          Upload a portrait and maintain the leader name and designation for the poster.
        </p>
      </div>
      <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
        <FaCheckCircle className={previewSrc ? "text-emerald-600" : "text-slate-400"} />
        <span>{previewSrc ? "Preview ready" : "Photo optional"}</span>
      </div>
    </div>

    <div className="mt-4 grid gap-4 lg:grid-cols-[140px,minmax(0,1fr)]">
      <div className="flex justify-center lg:justify-start">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt={nameValue || heading}
            className="h-32 w-32 rounded-full border-4 border-slate-200 object-cover shadow-sm"
          />
        ) : (
          <div className="flex h-32 w-32 items-center justify-center rounded-full border border-dashed border-slate-300 bg-slate-50 text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Upload
            <br />
            portrait
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Name</span>
            <input
              type="text"
              value={nameValue}
              onChange={(event) => onFieldChange(nameField, event.target.value)}
              className="w-full rounded-2xl border border-slate-300 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
              placeholder="Leader name"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Title</span>
            <input
              type="text"
              value={titleValue}
              onChange={(event) => onFieldChange(titleField, event.target.value)}
              className="w-full rounded-2xl border border-slate-300 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
              placeholder="Leader designation"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-slate-700">Photo</span>
          <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700">
                  <FaUpload />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {selectedFile ? selectedFile.name : "Upload JPG, PNG or WEBP"}
                  </p>
                  <FileUploadHint>Recommended portrait ratio for the cleanest poster layout.</FileUploadHint>
                </div>
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => onFileChange(event.target.files?.[0] || null)}
                className="block rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
              />
            </div>
          </div>
        </label>

        <div className="flex flex-wrap gap-2">
          {selectedFile ? (
            <button
              type="button"
              onClick={onClearSelectedFile}
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
            >
              Clear selected file
            </button>
          ) : null}
          {hasStoredPhoto && !selectedFile ? (
            <button
              type="button"
              onClick={onToggleRemovePhoto}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                removePhoto
                  ? "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
              }`}
            >
              {removePhoto ? "Keep saved photo" : "Remove saved photo"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

const OccasionPostersManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportingFormat, setExportingFormat] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [leaderOneFile, setLeaderOneFile] = useState(null);
  const [leaderTwoFile, setLeaderTwoFile] = useState(null);
  const [leaderOnePreviewSrc, setLeaderOnePreviewSrc] = useState("");
  const [leaderTwoPreviewSrc, setLeaderTwoPreviewSrc] = useState("");
  const [backgroundPreviewSrc, setBackgroundPreviewSrc] = useState("");
  const [backgroundLoading, setBackgroundLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await API.fetchOccasionPosters();
        if (!mounted) return;
        const safeRows = Array.isArray(data) ? data : [];
        setRows(safeRows);
        if (safeRows.length > 0) {
          setSelectedId((current) => current ?? safeRows[0].id);
        }
      } catch (error) {
        console.error("Failed to load occasion posters", error);
        if (mounted) {
          setRows([]);
          errorAlert("Load failed", error?.message || "Unable to load occasion posters.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedPoster = useMemo(
    () => rows.find((row) => Number(row.id) === Number(selectedId)) || rows[0] || null,
    [rows, selectedId]
  );

  useEffect(() => {
    if (!selectedPoster) return;
    setForm(buildFormFromPoster(selectedPoster));
    setBackgroundFile(null);
    setLeaderOneFile(null);
    setLeaderTwoFile(null);
  }, [selectedPoster]);

  useEffect(() => {
    let objectUrl = "";
    if (!selectedPoster || form.remove_leader_one_photo) {
      setLeaderOnePreviewSrc("");
      return undefined;
    }

    if (leaderOneFile) {
      objectUrl = URL.createObjectURL(leaderOneFile);
      setLeaderOnePreviewSrc(objectUrl);
    } else if (selectedPoster.leader_one_photo_path) {
      setLeaderOnePreviewSrc(
        resolveAssetUrl(selectedPoster.leader_one_photo_path, { folder: "occasion-posters" })
      );
    } else {
      setLeaderOnePreviewSrc("");
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    leaderOneFile,
    form.remove_leader_one_photo,
    selectedPoster,
  ]);

  useEffect(() => {
    let objectUrl = "";
    if (!selectedPoster || form.remove_leader_two_photo) {
      setLeaderTwoPreviewSrc("");
      return undefined;
    }

    if (leaderTwoFile) {
      objectUrl = URL.createObjectURL(leaderTwoFile);
      setLeaderTwoPreviewSrc(objectUrl);
    } else if (selectedPoster.leader_two_photo_path) {
      setLeaderTwoPreviewSrc(
        resolveAssetUrl(selectedPoster.leader_two_photo_path, { folder: "occasion-posters" })
      );
    } else {
      setLeaderTwoPreviewSrc("");
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    leaderTwoFile,
    form.remove_leader_two_photo,
    selectedPoster,
  ]);

  const filteredRows = useMemo(() => {
    const query = normalizeSearchToken(searchQuery);
    if (!query) return rows;

    return rows.filter((row) =>
      [
        row.title,
        row.date_label,
        row.quarter_label,
        row.category_label,
        row.tagline,
        row.theme_key,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [rows, searchQuery]);

  const groupedRows = useMemo(() => {
    return filteredRows.reduce((acc, row) => {
      const label = String(row.quarter_label || "Year-round");
      if (!acc[label]) acc[label] = [];
      acc[label].push(row);
      return acc;
    }, {});
  }, [filteredRows]);

  const stats = useMemo(() => {
    const activeCount = rows.filter((row) => !!row.is_active).length;
    const autoCount = rows.filter(
      (row) =>
        !String(row.custom_message || "").trim() &&
        !String(row.custom_notification_message || "").trim()
    ).length;

    return {
      total: rows.length,
      active: activeCount,
      auto: autoCount,
    };
  }, [rows]);

  const resolvedPreview = useMemo(() => {
    if (!selectedPoster) return null;

    return {
      ...selectedPoster,
      ...form,
      resolved_message:
        String(form.custom_message || "").trim() ||
        String(selectedPoster.default_message || "").trim(),
      resolved_notification_message:
        String(form.custom_notification_message || "").trim() ||
        String(selectedPoster.default_notification_message || "").trim(),
    };
  }, [selectedPoster, form]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    if (!selectedPoster) {
      setBackgroundPreviewSrc("");
      setBackgroundLoading(false);
      return undefined;
    }

    if (backgroundFile) {
      objectUrl = URL.createObjectURL(backgroundFile);
      setBackgroundPreviewSrc(objectUrl);
      setBackgroundLoading(false);
      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    if (!form.remove_background_image && selectedPoster.background_image_path) {
      setBackgroundPreviewSrc(
        resolveAssetUrl(selectedPoster.background_image_path, { folder: "occasion-posters" })
      );
      setBackgroundLoading(false);
      return undefined;
    }

    setBackgroundLoading(true);
    setBackgroundPreviewSrc("");
    resolvePosterBackgroundUrl({
      occasion_key: selectedPoster.occasion_key,
      title: selectedPoster.title,
      category_label: selectedPoster.category_label,
      theme_key: form.theme_key || selectedPoster.theme_key,
    })
      .then((url) => {
        if (!active) return;
        setBackgroundPreviewSrc(url || "");
      })
      .finally(() => {
        if (active) {
          setBackgroundLoading(false);
        }
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    backgroundFile,
    selectedPoster,
    selectedPoster?.background_image_path,
    selectedPoster?.occasion_key,
    selectedPoster?.title,
    selectedPoster?.category_label,
    selectedPoster?.theme_key,
    form.remove_background_image,
    form.theme_key,
  ]);

  const hasSavedBackground = Boolean(selectedPoster?.background_image_path);
  const usingCustomBackgroundPreview = Boolean(
    backgroundFile || (!form.remove_background_image && selectedPoster?.background_image_path)
  );

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggle = (field) => {
    setForm((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleLeaderOneFileChange = (file) => {
    setLeaderOneFile(file);
    setForm((prev) => ({
      ...prev,
      remove_leader_one_photo: false,
    }));
  };

  const handleLeaderTwoFileChange = (file) => {
    setLeaderTwoFile(file);
    setForm((prev) => ({
      ...prev,
      remove_leader_two_photo: false,
    }));
  };

  const handleBackgroundFileChange = (file) => {
    setBackgroundFile(file);
    setForm((prev) => ({
      ...prev,
      remove_background_image: false,
    }));
  };

  const handleSave = async () => {
    if (!selectedPoster) return;

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("tagline", form.tagline || "");
      formData.append("theme_key", form.theme_key || "");
      formData.append("custom_message", form.custom_message || "");
      formData.append("custom_notification_message", form.custom_notification_message || "");
      formData.append("remove_background_image", form.remove_background_image ? "1" : "0");
      formData.append("leader_one_name", form.leader_one_name || "");
      formData.append("leader_one_title", form.leader_one_title || "");
      formData.append("leader_two_name", form.leader_two_name || "");
      formData.append("leader_two_title", form.leader_two_title || "");
      formData.append("remove_leader_one_photo", form.remove_leader_one_photo ? "1" : "0");
      formData.append("remove_leader_two_photo", form.remove_leader_two_photo ? "1" : "0");
      formData.append("is_active", form.is_active ? "1" : "0");

      if (backgroundFile) {
        formData.append("background_image", backgroundFile);
      }
      if (leaderOneFile) {
        formData.append("leader_one_photo", leaderOneFile);
      }
      if (leaderTwoFile) {
        formData.append("leader_two_photo", leaderTwoFile);
      }

      const response = await API.updateOccasionPoster(selectedPoster.id, formData);
      const updatedRow = response?.data || response;
      if (!updatedRow?.id) {
        throw new Error("Unexpected server response.");
      }

      setRows((prev) =>
        prev.map((row) => (Number(row.id) === Number(updatedRow.id) ? updatedRow : row))
      );
      setSelectedId(updatedRow.id);
      successAlert("Poster saved", `${selectedPoster.title} updated successfully.`);
    } catch (error) {
      console.error("Failed to save occasion poster", error);
      errorAlert("Save failed", error?.message || "Unable to save occasion poster.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (format) => {
    if (!resolvedPreview) return;

    try {
      setExportingFormat(format);
      const canvas = await renderPosterCanvas({
        poster: resolvedPreview,
        leaderOnePreviewSrc,
        leaderTwoPreviewSrc,
        backgroundPreviewSrc,
      });
      const safeFileName = slugifyFileName(resolvedPreview.title);

      if (format === "pdf") {
        const imageData = canvas.toDataURL("image/jpeg", 0.94);
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: [POSTER_EXPORT_WIDTH, POSTER_EXPORT_HEIGHT],
        });
        pdf.addImage(
          imageData,
          "JPEG",
          0,
          0,
          POSTER_EXPORT_WIDTH,
          POSTER_EXPORT_HEIGHT
        );
        pdf.save(`${safeFileName}.pdf`);
      } else {
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (value) => {
              if (value) resolve(value);
              else reject(new Error("Failed to generate poster image."));
            },
            "image/jpeg",
            0.94
          );
        });
        downloadBlob(blob, `${safeFileName}.jpg`);
      }

      successAlert(
        "Poster downloaded",
        `${resolvedPreview.title} exported as ${format === "pdf" ? "PDF" : "JPG"}.`
      );
    } catch (error) {
      console.error("Failed to export poster", error);
      errorAlert(
        "Download failed",
        error?.message || "Unable to generate the poster download."
      );
    } finally {
      setExportingFormat("");
    }
  };

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen overflow-hidden bg-slate-100 mobile-page-pad">
        <div className="pointer-events-none absolute -left-20 top-16 hidden h-72 w-72 rounded-full bg-cyan-300/35 blur-3xl sm:block" />
        <div className="pointer-events-none absolute -right-20 top-24 hidden h-72 w-72 rounded-full bg-amber-300/30 blur-3xl sm:block" />
        <div className="pointer-events-none absolute inset-0 hidden bg-[linear-gradient(to_right,rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:30px_30px] sm:block" />

        <div className="relative mx-auto max-w-[1600px] px-3 py-4 sm:px-5 sm:py-6">
          <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-white/85 shadow-[0_24px_60px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="border-b border-slate-200 px-4 py-4 sm:px-6 sm:py-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-3xl">
                  <p className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-700">
                    Admin Content Studio
                  </p>
                  <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                    Occasion posters and auto wish templates
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
                    Manage the full occasion list, keep a fallback poster message and short notification for every day,
                    and upload two leader portraits with names and titles for a polished admin-ready poster.
                  </p>
                </div>

                <div className="grid w-full gap-3 sm:w-auto sm:grid-cols-3">
                  <div className="rounded-[22px] border border-cyan-200 bg-cyan-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700">
                      Total
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{stats.total}</p>
                  </div>
                  <div className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">
                      Active
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{stats.active}</p>
                  </div>
                  <div className="rounded-[22px] border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-700">
                      Auto Copy
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{stats.auto}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid xl:grid-cols-[340px,minmax(0,1fr)]">
              <aside className="border-b border-slate-200 bg-slate-50/70 p-4 xl:min-h-[1100px] xl:border-b-0 xl:border-r">
                <div className="rounded-[24px] border border-slate-200 bg-white px-3 py-3 shadow-sm">
                  <label className="relative block">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search occasion, date, theme..."
                      className="h-11 w-full rounded-2xl border border-slate-300 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                    />
                  </label>
                </div>

                <div className="mt-4 space-y-4 xl:max-h-[980px] xl:overflow-y-auto xl:pr-1">
                  {loading ? (
                    <div className="rounded-[24px] border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 shadow-sm">
                      Loading occasion posters...
                    </div>
                  ) : filteredRows.length === 0 ? (
                    <div className="rounded-[24px] border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 shadow-sm">
                      No occasion matched your search.
                    </div>
                  ) : (
                    Object.entries(groupedRows).map(([groupLabel, items]) => (
                      <div key={groupLabel}>
                        <div className="mb-2 flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-cyan-500" />
                          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                            {groupLabel}
                          </p>
                        </div>
                        <div className="space-y-2">
                          {items.map((row) => {
                            const isSelected = Number(row.id) === Number(selectedPoster?.id);
                            const usesAutoFallback =
                              !String(row.custom_message || "").trim() &&
                              !String(row.custom_notification_message || "").trim();

                            return (
                              <button
                                key={row.id}
                                type="button"
                                onClick={() => setSelectedId(row.id)}
                                className={`w-full rounded-[24px] border p-4 text-left transition ${
                                  isSelected
                                    ? "border-cyan-400 bg-cyan-50 shadow-[0_16px_30px_rgba(6,182,212,0.16)]"
                                    : "border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:bg-slate-50"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">{row.title}</p>
                                    <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                                      <FaCalendarAlt />
                                      <span>{row.date_label || "Date varies"}</span>
                                    </div>
                                  </div>
                                  <span
                                    className={`h-2.5 w-2.5 rounded-full ${
                                      row.is_active ? "bg-emerald-500" : "bg-slate-300"
                                    }`}
                                  />
                                </div>

                                <div className="mt-3 flex flex-wrap gap-1.5">
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                    {row.category_label}
                                  </span>
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                    {themeLabel(row.theme_key)}
                                  </span>
                                  <span
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                      usesAutoFallback
                                        ? "bg-amber-100 text-amber-700"
                                        : "bg-emerald-100 text-emerald-700"
                                    }`}
                                  >
                                    {usesAutoFallback ? "Auto fallback" : "Customised"}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </aside>

              <div className="p-4 sm:p-6">
                {!selectedPoster || !resolvedPreview ? (
                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
                    Select an occasion to start editing its poster details.
                  </div>
                ) : (
                  <div className="grid gap-5 2xl:grid-cols-[minmax(0,520px)_minmax(0,1fr)]">
                    <div className="space-y-5">
                      <div className="rounded-[28px] border border-slate-200 bg-slate-50/80 p-5 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">
                              Selected Occasion
                            </p>
                            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                              {selectedPoster.title}
                            </h2>
                            <p className="mt-2 text-sm leading-6 text-slate-600">
                              {selectedPoster.date_label || "Date varies"} | {selectedPoster.category_label} |{" "}
                              {selectedPoster.quarter_label}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleDownload("jpg")}
                              disabled={Boolean(exportingFormat)}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaImage />
                              <span>{exportingFormat === "jpg" ? "Preparing JPG..." : "Download JPG"}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownload("pdf")}
                              disabled={Boolean(exportingFormat)}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaFilePdf />
                              <span>{exportingFormat === "pdf" ? "Preparing PDF..." : "Download PDF"}</span>
                            </button>
                            <button
                              type="button"
                              onClick={handleSave}
                              disabled={saving || Boolean(exportingFormat)}
                              className="inline-flex items-center gap-2 rounded-full bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-cyan-300"
                            >
                              <FaSave />
                              <span>{saving ? "Saving..." : "Save changes"}</span>
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
                            Theme: {themeLabel(form.theme_key)}
                          </span>
                          <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
                            {form.is_active ? "Visible in schedule" : "Hidden until needed"}
                          </span>
                          <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
                            {String(form.custom_message || "").trim() ? "Custom poster copy" : "Automatic poster copy"}
                          </span>
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700">
                            <FaPalette />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900">Poster styling</h3>
                            <p className="text-sm text-slate-500">
                              Fine tune the theme and headline line used in the live preview.
                            </p>
                          </div>
	                        </div>
	
	                        <div className="mt-4 rounded-[24px] border border-slate-200 bg-slate-50 p-4">
	                          <div className="flex flex-wrap items-start justify-between gap-3">
	                            <div>
	                              <p className="text-sm font-semibold text-slate-800">Occasion background artwork</p>
	                              <p className="mt-1 text-sm text-slate-500">
	                                Upload your own beautiful event background. If you leave it empty, the poster uses
	                                the automatic occasion background.
	                              </p>
	                            </div>
	                            <span
	                              className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
	                                usingCustomBackgroundPreview
	                                  ? "bg-cyan-100 text-cyan-700"
	                                  : "bg-amber-100 text-amber-700"
	                              }`}
	                            >
	                              {usingCustomBackgroundPreview ? "Custom background" : "Automatic background"}
	                            </span>
	                          </div>

	                          <div className="mt-4 grid gap-4 lg:grid-cols-[220px,minmax(0,1fr)]">
	                            <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-sm">
	                              {backgroundPreviewSrc ? (
	                                <img
	                                  src={backgroundPreviewSrc}
	                                  alt={`${selectedPoster.title} background preview`}
	                                  className="aspect-[4/5] w-full object-cover"
	                                />
	                              ) : (
	                                <div className="flex aspect-[4/5] items-center justify-center px-4 text-center text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
	                                  Poster
	                                  <br />
	                                  background
	                                </div>
	                              )}
	                            </div>

	                            <div className="space-y-3">
	                              <label className="block">
	                                <span className="mb-1 block text-sm font-semibold text-slate-700">
	                                  Background image
	                                </span>
	                                <div className="rounded-[22px] border border-dashed border-slate-300 bg-white px-4 py-4">
	                                  <div className="flex flex-wrap items-center justify-between gap-3">
	                                    <div className="flex items-center gap-3">
	                                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700">
	                                        <FaUpload />
	                                      </div>
	                                      <div>
	                                        <p className="text-sm font-semibold text-slate-800">
	                                          {backgroundFile ? backgroundFile.name : "Upload JPG, PNG or WEBP"}
	                                        </p>
	                                        <FileUploadHint>
	                                          Best results: vertical creative image with one clean text area.
	                                        </FileUploadHint>
	                                      </div>
	                                    </div>
	                                    <input
	                                      type="file"
	                                      accept="image/png,image/jpeg,image/webp"
	                                      onChange={(event) =>
	                                        handleBackgroundFileChange(event.target.files?.[0] || null)
	                                      }
	                                      className="block rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
	                                    />
	                                  </div>
	                                </div>
	                              </label>

	                              <div className="flex flex-wrap gap-2">
	                                {backgroundFile ? (
	                                  <button
	                                    type="button"
	                                    onClick={() => setBackgroundFile(null)}
	                                    className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
	                                  >
	                                    Clear selected file
	                                  </button>
	                                ) : null}
	                                {hasSavedBackground && !backgroundFile ? (
	                                  <button
	                                    type="button"
	                                    onClick={() => handleToggle("remove_background_image")}
	                                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
	                                      form.remove_background_image
	                                        ? "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
	                                        : "border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
	                                    }`}
	                                  >
	                                    {form.remove_background_image
	                                      ? "Keep saved background"
	                                      : "Remove saved background"}
	                                  </button>
	                                ) : null}
	                              </div>
	                            </div>
	                          </div>
	                        </div>

	                        <div className="mt-4 grid gap-4 md:grid-cols-2">
	                          <label className="block">
	                            <span className="mb-1 block text-sm font-semibold text-slate-700">Theme</span>
	                            <select
                              value={form.theme_key}
                              onChange={(event) => handleFieldChange("theme_key", event.target.value)}
                              className="h-12 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                            >
                              {Object.entries(THEME_PRESETS).map(([key, value]) => (
                                <option key={key} value={key}>
                                  {value.label}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label className="flex items-end rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3">
                            <input
                              type="checkbox"
                              checked={form.is_active}
                              onChange={() => handleToggle("is_active")}
                              className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                            />
                            <div className="ml-3">
                              <p className="text-sm font-semibold text-slate-800">Keep this occasion active</p>
                              <p className="text-xs text-slate-500">
                                Turn this off if you want to hide it temporarily from the active list.
                              </p>
                            </div>
                          </label>
                        </div>

                        <label className="mt-4 block">
                          <span className="mb-1 block text-sm font-semibold text-slate-700">Tagline</span>
                          <input
                            type="text"
                            value={form.tagline}
                            onChange={(event) => handleFieldChange("tagline", event.target.value)}
                            className="w-full rounded-2xl border border-slate-300 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                            placeholder="Short line below the title"
                          />
                          <FileUploadHint>
                            Keep it crisp. This line helps the poster shift mood depending on the occasion.
                          </FileUploadHint>
                        </label>
                      </div>

                      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                              <FaMagic />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">Poster message</h3>
                              <p className="text-sm text-slate-500">
                                Leave blank and the automatic occasion message will be used.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleFieldChange("custom_message", "")}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                          >
                            <FaSyncAlt />
                            <span>Reset to auto</span>
                          </button>
                        </div>

                        <textarea
                          value={form.custom_message}
                          onChange={(event) => handleFieldChange("custom_message", event.target.value)}
                          rows={7}
                          className="mt-4 w-full rounded-[24px] border border-slate-300 px-4 py-3 text-sm leading-7 text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                          placeholder={selectedPoster.default_message || "Automatic poster message"}
                        />
                        <FileUploadHint>
                          Default message: {selectedPoster.default_message}
                        </FileUploadHint>
                      </div>

                      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                              <FaBell />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">Notification message</h3>
                              <p className="text-sm text-slate-500">
                                A short app-style message preview is generated automatically if you keep this blank.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleFieldChange("custom_notification_message", "")}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                          >
                            <FaSyncAlt />
                            <span>Reset to auto</span>
                          </button>
                        </div>

                        <textarea
                          value={form.custom_notification_message}
                          onChange={(event) =>
                            handleFieldChange("custom_notification_message", event.target.value)
                          }
                          rows={4}
                          className="mt-4 w-full rounded-[24px] border border-slate-300 px-4 py-3 text-sm leading-7 text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                          placeholder={selectedPoster.default_notification_message || "Automatic notification message"}
                        />
                        <FileUploadHint>
                          Default notification: {selectedPoster.default_notification_message}
                        </FileUploadHint>
                      </div>

                      <LeaderEditor
                        heading="Leader 1"
                        nameField="leader_one_name"
                        titleField="leader_one_title"
                        nameValue={form.leader_one_name}
                        titleValue={form.leader_one_title}
                        previewSrc={leaderOnePreviewSrc}
                        hasStoredPhoto={Boolean(selectedPoster.leader_one_photo_path)}
                        removePhoto={form.remove_leader_one_photo}
                        onFieldChange={handleFieldChange}
                        onFileChange={handleLeaderOneFileChange}
                        onToggleRemovePhoto={() => handleToggle("remove_leader_one_photo")}
                        onClearSelectedFile={() => setLeaderOneFile(null)}
                        selectedFile={leaderOneFile}
                      />

                      <LeaderEditor
                        heading="Leader 2"
                        nameField="leader_two_name"
                        titleField="leader_two_title"
                        nameValue={form.leader_two_name}
                        titleValue={form.leader_two_title}
                        previewSrc={leaderTwoPreviewSrc}
                        hasStoredPhoto={Boolean(selectedPoster.leader_two_photo_path)}
                        removePhoto={form.remove_leader_two_photo}
                        onFieldChange={handleFieldChange}
                        onFileChange={handleLeaderTwoFileChange}
                        onToggleRemovePhoto={() => handleToggle("remove_leader_two_photo")}
                        onClearSelectedFile={() => setLeaderTwoFile(null)}
                        selectedFile={leaderTwoFile}
                      />
                    </div>

	                    <OccasionPosterPreview
	                      poster={resolvedPreview}
	                      leaderOnePreviewSrc={leaderOnePreviewSrc}
	                      leaderTwoPreviewSrc={leaderTwoPreviewSrc}
	                      backgroundPreviewSrc={backgroundPreviewSrc}
	                      backgroundLoading={backgroundLoading}
	                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OccasionPostersManager;
